import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Empty, Badge, Modal, Field, Input } from "@/components/ui-kit";
import { fmtDate } from "@/lib/utils";
import { HandHelping, Plus, Pencil, Trash2, Clock, MapPin, Check, X, Users, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function Volunteers() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const emptySlot = () => ({ id: null, start: "09:00", end: "13:00", task: "", needed: 1 });
  const empty = () => ({ title: "", date: new Date().toISOString().slice(0, 10), location: "", note: "", slots: [emptySlot()] });

  const load = () => api.get("/volunteer/plans").then((r) => setPlans(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const canManage = plans[0]?.can_manage ?? (user?.role === "admin");

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, slots: (form.slots || []).filter((s) => s.task.trim() || s.start || s.end) };
    try {
      if (form.id) await api.put(`/volunteer/plans/${form.id}`, payload);
      else await api.post("/volunteer/plans", payload);
      toast.success("Planning enregistré"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce planning ?")) return;
    try { await api.delete(`/volunteer/plans/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggleAvail = async (planId, slotId) => {
    try { const { data } = await api.post(`/volunteer/plans/${planId}/slots/${slotId}/availability`); setPlans((ps) => ps.map((p) => (p.id === planId ? data : p))); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggleConfirm = async (planId, slotId, userId) => {
    try { const { data } = await api.post(`/volunteer/plans/${planId}/slots/${slotId}/confirm`, { user_id: userId }); setPlans((ps) => ps.map((p) => (p.id === planId ? data : p))); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openNew = () => { setForm(empty()); setOpen(true); };
  const openEdit = (p) => { setForm({ id: p.id, title: p.title, date: (p.date || "").slice(0, 10), location: p.location, note: p.note, slots: p.slots.map((s) => ({ id: s.id, start: s.start, end: s.end, task: s.task, needed: s.needed })) }); setOpen(true); };

  const setSlot = (i, key, val) => setForm((f) => ({ ...f, slots: f.slots.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));
  const addSlot = () => setForm((f) => ({ ...f, slots: [...f.slots, emptySlot()] }));
  const delSlot = (i) => setForm((f) => ({ ...f, slots: f.slots.filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="volunteers-page">
      <PageHeader title="Bénévoles" subtitle="Plannings par créneaux : indiquez vos disponibilités, les responsables confirment qui est retenu." />

      {canManage && (
        <div className="flex justify-end mb-4">
          <Button data-testid="add-plan-btn" onClick={openNew}><Plus size={16} /> Nouveau planning</Button>
        </div>
      )}

      {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Chargement...</p>
        : plans.length ? (
          <div className="space-y-5">
            {plans.map((p) => (
              <Card key={p.id} data-testid="volunteer-plan">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h3 className="font-bold flex items-center gap-2"><HandHelping size={17} className="text-primary shrink-0" /> {p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                      <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(p.date)}</span>
                      {p.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
                    </p>
                    {p.note && <p className="text-xs text-muted-foreground mt-1 italic">{p.note}</p>}
                  </div>
                  {p.can_manage && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`edit-plan-${p.id}`} onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                      <button data-testid={`delete-plan-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid={`plan-table-${p.id}`}>
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="py-2 pr-3 text-xs uppercase tracking-widest text-muted-foreground font-bold whitespace-nowrap">Horaire</th>
                        <th className="py-2 pr-3 text-xs uppercase tracking-widest text-muted-foreground font-bold">Tâche</th>
                        <th className="py-2 pr-3 text-xs uppercase tracking-widest text-muted-foreground font-bold whitespace-nowrap">Retenus</th>
                        <th className="py-2 pr-3 text-xs uppercase tracking-widest text-muted-foreground font-bold">Bénévoles disponibles</th>
                        <th className="py-2 text-xs uppercase tracking-widest text-muted-foreground font-bold text-right whitespace-nowrap">Ma dispo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.slots.length ? p.slots.map((s) => {
                        const full = s.needed > 0 && s.confirmed_count >= s.needed;
                        return (
                          <tr key={s.id} data-testid={`slot-row-${s.id}`} className="border-b border-border/50 align-top">
                            <td className="py-2.5 pr-3 font-semibold whitespace-nowrap"><span className="inline-flex items-center gap-1"><Clock size={13} className="text-muted-foreground" /> {s.start}–{s.end}</span></td>
                            <td className="py-2.5 pr-3 font-medium">{s.task || "—"}</td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              <Badge color={full ? "green" : "yellow"}>{s.confirmed_count}{s.needed > 0 ? ` / ${s.needed}` : ""}</Badge>
                            </td>
                            <td className="py-2.5 pr-3">
                              {s.availability.length ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {s.availability.map((a) => {
                                    const conf = s.confirmed.some((c) => c.user_id === a.user_id);
                                    return p.can_manage ? (
                                      <button key={a.user_id} data-testid={`confirm-${s.id}-${a.user_id}`} onClick={() => toggleConfirm(p.id, s.id, a.user_id)}
                                        title={conf ? "Retirer la sélection" : "Confirmer ce bénévole"}
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border transition-colors ${conf ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-primary"}`}>
                                        {conf ? <Check size={12} /> : null} {a.name}
                                      </button>
                                    ) : (
                                      <span key={a.user_id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${conf ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                                        {conf ? <Check size={12} /> : null} {a.name}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : <span className="text-xs text-muted-foreground/60">Personne pour l'instant</span>}
                            </td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                              <button data-testid={`avail-${s.id}`} onClick={() => toggleAvail(p.id, s.id)}
                                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${s.my_available ? "bg-primary/15 text-primary hover:bg-destructive/15 hover:text-destructive" : "bg-secondary text-muted-foreground hover:text-primary"}`}>
                                {s.my_available ? <><X size={13} /> Retirer</> : <><Check size={13} /> Je suis dispo</>}
                              </button>
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr><td colSpan={5} className="py-3 text-xs text-muted-foreground text-center">Aucun créneau pour ce planning.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
          </div>
        ) : <Empty text="Aucun planning bénévole. Un responsable peut en créer un." />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier le planning" : "Nouveau planning bénévole"}>
        {form && (
          <form onSubmit={save}>
            <Field label="Titre / événement"><Input data-testid="plan-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Tournoi de Pâques" required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><Input data-testid="plan-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></Field>
              <Field label="Lieu (optionnel)"><Input data-testid="plan-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex. Hall omnisports" /></Field>
            </div>
            <Field label="Note (optionnel)"><Input data-testid="plan-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Consignes générales…" /></Field>

            <div className="mt-2 mb-1 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Créneaux (horaire + tâche)</span>
              <button type="button" data-testid="add-slot-btn" onClick={addSlot} className="text-xs font-bold text-primary hover:underline flex items-center gap-1"><Plus size={13} /> Ajouter</button>
            </div>
            <div className="space-y-2">
              {form.slots.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto_auto] gap-2 items-center" data-testid={`slot-edit-${i}`}>
                  <Input data-testid={`slot-start-${i}`} type="time" value={s.start} onChange={(e) => setSlot(i, "start", e.target.value)} />
                  <Input data-testid={`slot-end-${i}`} type="time" value={s.end} onChange={(e) => setSlot(i, "end", e.target.value)} />
                  <Input data-testid={`slot-task-${i}`} value={s.task} onChange={(e) => setSlot(i, "task", e.target.value)} placeholder="Tâche (ex. Buvette)" />
                  <Input data-testid={`slot-needed-${i}`} type="number" min="0" value={s.needed} onChange={(e) => setSlot(i, "needed", e.target.value)} className="w-16" title="Nombre de bénévoles souhaité" />
                  <button type="button" data-testid={`del-slot-${i}`} onClick={() => delSlot(i)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <Button data-testid="save-plan-btn" className="w-full mt-4">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}
